import { FC, ChangeEvent, useRef } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { TYPE_FILES_ALLOWED } from 'constants/defaultConstants';

import { isDocumentFileAllowed } from 'utils/isDocumentFileAllowed';
import errorSwal from 'utils/errorSwal';

import Button from 'modules/_shared/components/Buttons';

import { useSendCommunicationModal } from '../context/SendCommunicationModalContext';

import AttachmentChip from './AttachmentChip';

const MAX_ATTACHMENTS = 5;

const AttachmentSection: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useSendCommunicationModal();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isButtonDisabled = data.attachments.length >= MAX_ATTACHMENTS;

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (data.attachments.length >= MAX_ATTACHMENTS) {
      return;
    }

    const files = Array.from(event.target.files ?? []);

    if (files.length === 0) return;

    const validationResults = await Promise.all(
      files.map((file) => isDocumentFileAllowed(file))
    );

    const hasInvalidFiles = validationResults.some((isValid) => !isValid);

    if (hasInvalidFiles) {
      errorSwal(t('InvalidFile'));
      return;
    }

    actions.handleAddAttachments(files);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="attachment-section mt-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center">
          <em className="icon ni ni-clip" />
          <h6 className="mr-1 my-0">{t('Attachments')}</h6>
          {data.attachments.length > 0 && (
            <span className="badge badge-pill badge-primary">
              {data.attachments.length}
            </span>
          )}
        </div>
        <Button
          onClick={() => fileInputRef.current?.click()}
          icon="ni ni-plus"
          outlineStyle
          disabled={isButtonDisabled}
        >
          {t('Add')}
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="d-none"
        accept={TYPE_FILES_ALLOWED}
        multiple
        onChange={handleFileChange}
      />

      {data.attachments.length > 0 && (
        <div className="attachment-section__list d-flex flex-wrap">
          {data.attachments.map((file, index) => (
            <AttachmentChip
              // eslint-disable-next-line react/no-array-index-key
              key={`${file.name}-${index}`}
              file={file}
              index={index}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AttachmentSection;
